import { HashRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { SapiensAuthProvider, useAutoLogin } from '@sapiens/auth-react';
import { useSnackbar } from 'notistack';

import { lightTheme } from '@/theme';
import { setTokenGetter, setUnauthorizedHandler } from '@/api';

// Pages
import { HomePage } from '@/pages/home';
import {
  LoginPage,
  ActivationRequestPage,
  ActivationCompletePage,
  RecoveryRequestPage,
  RecoveryCompletePage,
} from '@/pages/auth';
import { SsoLoginPage } from '@/pages/sso';
import { ProfilePage } from '@/pages/profile';
import { UserListPage, CreateUserPage, EditUserPage, AuditLogPage } from '@/pages/admin';
import { UnauthorizedPage, NotFoundPage } from '@/pages/error';

// Components
import { RequireAuth, RequireAdmin } from '@/components/common';

// Auth configuration
const authConfig = {
  apiBaseUrl: '',  // Use proxy in development
  ssoLoginUrl: '/#/ssoLogin',
};

/**
 * App content with auth integration
 */
function AppContent() {
  const { enqueueSnackbar } = useSnackbar();

  // Set up auto-login with toast notifications
  useAutoLogin({
    onStart: () => {
      enqueueSnackbar('Re-authenticating...', { variant: 'info' });
    },
    onSuccess: () => {
      enqueueSnackbar('Re-authentication successful', { variant: 'success' });
    },
    onError: () => {
      enqueueSnackbar('Session expired, please login again', { variant: 'warning' });
    },
  });

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/activation" element={<ActivationRequestPage />} />
      <Route path="/activation/complete" element={<ActivationCompletePage />} />
      <Route path="/recovery" element={<RecoveryRequestPage />} />
      <Route path="/recovery/complete" element={<RecoveryCompletePage />} />
      <Route path="/ssoLogin" element={<SsoLoginPage />} />
      
      {/* Protected routes - require authentication */}
      <Route
        path="/profile"
        element={
          <RequireAuth>
            <ProfilePage />
          </RequireAuth>
        }
      />

      {/* Admin routes - require admin role */}
      <Route
        path="/admin/users"
        element={
          <RequireAuth>
            <RequireAdmin>
              <UserListPage />
            </RequireAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/admin/users/create"
        element={
          <RequireAuth>
            <RequireAdmin>
              <CreateUserPage />
            </RequireAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/admin/users/:id/edit"
        element={
          <RequireAuth>
            <RequireAdmin>
              <EditUserPage />
            </RequireAdmin>
          </RequireAuth>
        }
      />
      <Route
        path="/admin/audit"
        element={
          <RequireAuth>
            <RequireAdmin>
              <AuditLogPage />
            </RequireAdmin>
          </RequireAuth>
        }
      />

      {/* Error routes */}
      <Route path="/unauthorized" element={<UnauthorizedPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

/**
 * Auth provider wrapper that sets up API client integration
 */
function AuthProviderWrapper({ children }: { children: React.ReactNode }) {
  return (
    <SapiensAuthProvider
      config={authConfig}
      onAutoLoginStart={() => {
        // Handled in AppContent
      }}
    >
      <AuthClientIntegration />
      {children}
    </SapiensAuthProvider>
  );
}

/**
 * Integrates auth client with API client
 */
function AuthClientIntegration() {
  const { enqueueSnackbar } = useSnackbar();

  // This effect runs once to set up the integration
  // We use a ref pattern via the setTokenGetter function
  setTokenGetter(() => {
    // Access token from localStorage directly since we can't use hooks here
    const token = localStorage.getItem('sapiens_auth_token');
    const expiresAt = localStorage.getItem('sapiens_auth_expires_at');
    
    if (!token || !expiresAt) return null;
    if (Date.now() > parseInt(expiresAt, 10)) return null;
    
    return token;
  });

  setUnauthorizedHandler(() => {
    enqueueSnackbar('Session expired, please login again', { variant: 'warning' });
    // Navigation will be handled by the auth state change
  });

  return null;
}

export default function App() {
  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        autoHideDuration={3000}
      >
        <HashRouter>
          <AuthProviderWrapper>
            <AppContent />
          </AuthProviderWrapper>
        </HashRouter>
      </SnackbarProvider>
    </ThemeProvider>
  );
}
